import { useMemo } from 'react'
import { Link2, X } from 'lucide-react'
import {
  describeDependencies,
  blockerLabel,
  blockerNote,
  withoutDependency,
} from '@trackmind/core'
import type { Task } from '@/types'
import { useApp } from '@/context/AppContext'
import { useToday } from '@/hooks/useToday'
import { InfoTip } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

/**
 * What a task is waiting on — stated, not alarmed about.
 *
 * This used to be a red-tinted card and a red "Blocked" badge. Nothing was
 * ever stopping you ticking the task off, and the scheduler places it later
 * the same day anyway, so the alarm was for a state that didn't exist. It's
 * information: "After Draft intro", in the same grey as everything else.
 *
 * Each chip carries its own remove button, which is the whole answer to "how
 * do I get rid of a dependency" — previously that meant opening the task,
 * scrolling to a list of every task you own, and unticking one.
 *
 * A blocker in another project names that project, because "After Draft
 * intro" is useless when the thing lives somewhere you aren't looking. And a
 * blocker the scheduler is quietly ignoring — paused, archived, in a paused
 * project — says so, since otherwise the app appears to be holding work back
 * that it has in fact already released.
 */
export function BlockerChips({ task, className }: { task: Task; className?: string }) {
  const { tasks, projects, saveTask } = useApp()
  const todayKey = useToday()

  const infos = useMemo(
    () => describeDependencies(task, tasks, projects, todayKey()).filter((b) => b.reason !== 'completed'),
    [task, tasks, projects, todayKey],
  )

  if (infos.length === 0) return null

  const remove = async (dependencyId: string) => {
    await saveTask({
      ...task,
      dependencies: withoutDependency(task, dependencyId),
      updated_at: new Date().toISOString(),
    })
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      {infos.map((info) => {
        const note = blockerNote(info)
        return (
          <span
            key={info.id}
            className={cn(
              'inline-flex max-w-full items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px]',
              info.holding
                ? 'border-border text-muted-foreground'
                : 'border-dashed text-muted-foreground/70',
            )}
          >
            <Link2 className="h-3 w-3 shrink-0" />
            <InfoTip
              label={
                info.holding
                  ? `Waits for "${blockerLabel(info)}" to be scheduled first. You can still finish this whenever you like.`
                  : `Was waiting for "${blockerLabel(info)}" — ${note}.`
              }
            >
              <span className="truncate">
                After {blockerLabel(info)}
                {!info.holding && <span className="ml-1 italic">({note})</span>}
              </span>
            </InfoTip>
            <button
              type="button"
              aria-label={`Remove dependency on ${info.title}`}
              onClick={(e) => { e.stopPropagation(); void remove(info.id) }}
              className="shrink-0 rounded p-0.5 hover:bg-accent hover:text-foreground"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        )
      })}
    </div>
  )
}
